// Vite's per-build chunk filenames are content-hashed. A deploy that lands
// while a tab sits open leaves that tab's already-loaded JS referencing
// chunk files the server no longer has (the FTP deploy replaces app/dist/
// wholesale each push) — clicking into a not-yet-visited lazy route then
// 404s on the dynamic import. The fix is just a fresh load, which picks up
// the new index.html and correct chunk hashes. Reloads once per tab session
// so a genuinely broken chunk (not just staleness) doesn't loop forever.

const RELOAD_FLAG_KEY = 'hs_chunk_reload_attempted';

export const CHUNK_ERROR_PATTERN = /dynamically imported module|importing a module script failed|failed to fetch/i;

export function alreadyReloadedForStaleChunk(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

/** Clears the reload guard once the app is confirmed rendering
 * successfully (see the route-change effect in App.tsx). Without this,
 * the guard — needed to stop one truly-broken chunk from reload-looping —
 * would also block the auto-recovery for any *later, unrelated* stale
 * chunk for the rest of the tab's session, so a second deploy landing
 * while the same tab stays open would show the manual "Something went
 * wrong" screen instead of quietly reloading like the first one did. */
export function clearStaleChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
  } catch {
    // sessionStorage unavailable — nothing to clear.
  }
}

/** Reloads the page once per tab session. Returns false (and does nothing)
 * if that reload already happened, so callers can fall back to a manual
 * "please refresh" UI instead of looping. */
export function reloadForStaleChunk(): boolean {
  if (alreadyReloadedForStaleChunk()) return false;
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
  } catch {
    // sessionStorage unavailable (e.g. private browsing) — reload anyway,
    // just without the loop guard.
  }
  window.location.reload();
  return true;
}
